import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useMemo } from "react";

import { useBulkUpdateRoutesMutation, useCompetitionRoutesQuery } from "@/hooks/routes";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useModal } from "./useModal";

export default function PostConfirmCompetitionResults({
  competitionUuid,
}: {
  competitionUuid: string;
}) {
  const { close } = useModal();
  const venueUuid = useCurrentVenueUuid();
  const { data: competitionRoutes } = useCompetitionRoutesQuery(competitionUuid, venueUuid);

  const routesToStrip = useMemo(() => {
    if (!competitionRoutes) return [];
    return competitionRoutes.map((route) => route.uuid);
  }, [competitionRoutes]);

  const stripMutation = useBulkUpdateRoutesMutation();

  const onConfirm = () => {
    if (routesToStrip.length === 0) {
      close();
      return;
    }

    const updates = routesToStrip.map((uuid) => ({
      uuid,
      endTime: "NOW",
    }));

    stripMutation.mutate(updates, {
      onSuccess: () => {
        close();
      },
      onError: (_err) => {
        alert("Failed to strip routes. Please try again or contact support");
      },
    });
  };

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Strip competition routes?</DialogTitle>
      <DialogContent>
        <Typography sx={{ maxWidth: 400 }}>
          The competition results have been published and the routes have been moved into a circuit.
          <br /> Do you want to strip them?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          color="danger"
          onClick={onConfirm}
          sx={{ flex: 1 }}
          loading={stripMutation.isPending}
          disabled={routesToStrip.length === 0}
        >
          Yes, strip them
        </Button>
        <Button color="neutral" variant="soft" onClick={close} sx={{ flex: 1 }}>
          No, leave them
        </Button>
      </DialogActions>
    </ModalDialog>
  );
}
